import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATHS } from 'config/paths';

/**
 * Back navigation that never strands the user. `navigate(-1)` walks the browser history,
 * so when the app was opened on this page directly (a push-notification/magic deep link
 * or a refresh) there is nothing to go back to and it would leave the PWA entirely.
 *
 * React Router's history records a monotonically increasing `idx` in `window.history.state`
 * (0 for the first entry of the session). When `idx > 0` there is an in-app entry behind us,
 * so we go back; otherwise we replace with a safe fallback (the weeks home by default).
 */
export const useGoBack = (fallback: string = PATHS.weeks): (() => void) => {
  const navigate = useNavigate();

  return useCallback(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) {
      navigate(-1);
    } else {
      navigate(fallback, { replace: true });
    }
  }, [navigate, fallback]);
};
